import { useState, useMemo } from 'react';
import { Check, Package } from 'lucide-react';
import { CURRENCIES, type InventoryItem } from '@siliconvault/shared';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useCreateOffer } from '../../hooks/useCommunity';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';
import styles from './CreateOfferForm.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  toUserId: string;
  toUserName: string;
  /** Items the target user has listed (for-trade / for-sale) */
  targetItems: InventoryItem[];
  /** Items the sender owns */
  ownItems: InventoryItem[];
  /** Pre-selected item to request */
  preSelectedItemId?: string;
}

export function CreateOfferForm({
  isOpen, onClose, toUserId, toUserName,
  targetItems, ownItems, preSelectedItemId,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const createMutation = useCreateOffer();

  const [requestedIds, setRequestedIds] = useState<Set<string>>(
    preSelectedItemId ? new Set([preSelectedItemId]) : new Set()
  );
  const [offeredIds, setOfferedIds] = useState<Set<string>>(new Set());
  const [money, setMoney] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [message, setMessage] = useState('');
  const [requestSearch, setRequestSearch] = useState('');
  const [offerSearch, setOfferSearch] = useState('');

  const filteredTargetItems = useMemo(
    () =>
      requestSearch
        ? targetItems.filter((item) =>
            item.name.toLowerCase().includes(requestSearch.toLowerCase())
          )
        : targetItems,
    [targetItems, requestSearch]
  );

  const filteredOwnItems = useMemo(
    () =>
      offerSearch
        ? ownItems.filter((item) =>
            item.name.toLowerCase().includes(offerSearch.toLowerCase())
          )
        : ownItems,
    [ownItems, offerSearch]
  );

  function toggleRequested(id: string) {
    setRequestedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleOffered(id: string) {
    setOfferedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    if (requestedIds.size === 0) return;
    try {
      await createMutation.mutateAsync({
        toUserId,
        requestedItemIds: Array.from(requestedIds),
        offeredItemIds: Array.from(offeredIds),
        moneyAmount: money || null,
        moneyCurrency: currency as any,
        message,
      });
      toast(t('community.offerSent'), 'success');
      onClose();
    } catch (err: any) {
      toast(err?.message || t('community.offerSendFailed'), 'error');
    }
  }

  function renderItemRow(
    item: InventoryItem,
    isSelected: boolean,
    onToggle: () => void,
  ) {
    return (
      <div
        key={item.id}
        className={`${styles.pickerItem} ${isSelected ? styles.pickerItemSelected : ''}`}
        onClick={onToggle}
      >
        <div className={`${styles.pickerItemCheck} ${isSelected ? styles.pickerItemCheckActive : ''}`}>
          {isSelected && <Check size={10} strokeWidth={2.5} />}
        </div>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} className={styles.pickerItemThumb} />
        ) : (
          <div className={styles.pickerItemThumbPlaceholder}>
            <Package size={14} strokeWidth={1.5} />
          </div>
        )}
        <div className={styles.pickerItemBody}>
          <div className={styles.pickerItemName}>{item.name}</div>
          <div className={styles.pickerItemMeta}>
            {item.platform} · {item.condition}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('community.makeOffer')} — ${toUserName}`}
      wide
      footer={
        <>
          {createMutation.isError && (
            <span className={styles.formError}>
              {createMutation.error?.message || t('community.offerSendFailed')}
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={requestedIds.size === 0 || createMutation.isPending}>
            {createMutation.isPending ? t('community.sending') : t('community.sendOffer')}
          </Button>
        </>
      }
    >
      <div className={styles.content}>
        {/* Two-column item picker */}
        <div className={styles.pickerColumns}>
          {/* Left column: items to request */}
          <div className={styles.pickerColumn}>
            <div className={styles.columnHeader}>
              <span className={styles.columnTitle}>{t('community.requestedItems')}</span>
              <span className={styles.columnCount}>{requestedIds.size}</span>
            </div>
            {targetItems.length > 3 && (
              <input
                className={styles.pickerSearch}
                type="text"
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
                placeholder={t('community.searchItems')}
              />
            )}
            <div className={styles.pickerList}>
              {filteredTargetItems.map((item) =>
                renderItemRow(item, requestedIds.has(item.id), () => toggleRequested(item.id))
              )}
              {filteredTargetItems.length === 0 && (
                <div className={styles.emptyState}>{t('community.noListedItems')}</div>
              )}
            </div>
          </div>

          {/* Right column: items to offer */}
          <div className={styles.pickerColumn}>
            <div className={styles.columnHeader}>
              <span className={styles.columnTitle}>{t('community.offeredItems')}</span>
              <span className={styles.columnCount}>{offeredIds.size}</span>
            </div>
            {ownItems.length > 3 && (
              <input
                className={styles.pickerSearch}
                type="text"
                value={offerSearch}
                onChange={(e) => setOfferSearch(e.target.value)}
                placeholder={t('community.searchItems')}
              />
            )}
            <div className={styles.pickerList}>
              {filteredOwnItems.map((item) =>
                renderItemRow(item, offeredIds.has(item.id), () => toggleOffered(item.id))
              )}
              {filteredOwnItems.length === 0 && (
                <div className={styles.emptyState}>{t('community.noOwnItems')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Message + money row */}
        <div className={styles.detailsRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('community.message')}</label>
            <textarea
              className={styles.formTextarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder={t('community.messagePlaceholder')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('community.cashComponent')}</label>
            <div className={styles.priceInputWrap}>
              <select
                className={styles.priceCurrency}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                className={styles.priceInput}
                type="text"
                value={money}
                onChange={(e) => setMoney(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
